import React from 'react';
import { SvgIcon, Tooltip } from '@mui/material';
import MedicationLiquidRounded from '@mui/icons-material/MedicationLiquidRounded';
import VaccinesRounded from '@mui/icons-material/VaccinesRounded';
import MedicationRounded from '@mui/icons-material/MedicationRounded';
import './OrderPages.css';

// Stock colour coding for the product header: red = out of stock, yellow = low stock, blue = sufficient stock.
export const getBackgroundColor = (stock) => {
  if (Number(stock) === 0) {
    return '#ffcccc'; // Red for out of stock
  } else if (stock < 20) {
    return '#fff0b3'; // Yellow for low stock
  } else {
    return '#e3f2fd'; // Blue for sufficient stock
  }
};

// "10.00%" -> "10%", "16.67%" -> "16.67%"; empty, "N/A" or zero schemes give null.
export const schemePercent = (scheme) => {
  const value = parseFloat(String(scheme || '').replace('%', ''));
  return value > 0 ? `${Number(value.toFixed(2))}%` : null;
};

export const SchemeRibbon = ({ scheme }) => {
  const percent = schemePercent(scheme);
  if (!percent) return null;
  return (
    <span className="scheme-ribbon" aria-label={`Get flat ${percent} scheme`}>
      <span className="scheme-ribbon-tab">
        <span className="scheme-ribbon-get">Get</span>
        <span className="scheme-ribbon-fold" aria-hidden="true" />
      </span>
      <span className="scheme-ribbon-offer">Flat <strong>{percent}</strong> Scheme</span>
    </span>
  );
};

// A round scored tablet beside a capsule.
const TabletIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11Zm-3.6 4.6h7.2a.9.9 0 0 1 0 1.8H5.4a.9.9 0 0 1 0-1.8Z" />
    <path d="M17.2 10.6a3.1 3.1 0 0 1 4.4 4.4l-4.6 4.6a3.1 3.1 0 0 1-4.4-4.4l4.6-4.6Zm-3.3 6.2a1.3 1.3 0 0 0 1.84 1.84l2.1-2.1-1.84-1.84-2.1 2.1Z" />
  </SvgIcon>
);

// A pump bottle with a label band.
const LotionIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M8.5 2H15v2h-2.2v2h2.2a1 1 0 0 1 1 1v1.3A3 3 0 0 1 18 11.2V20a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2v-8.8a3 3 0 0 1 2-2.9V7a1 1 0 0 1 1-1h2.2V4H8.5V2Zm-.3 11.2h7.6v4.4H8.2v-4.4Z" />
  </SvgIcon>
);

// A dropper releasing a drop.
const DropsIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M10 2h4a1.5 1.5 0 0 1 1.5 1.5V6a1.5 1.5 0 0 1-1 1.4V9h-5V7.4A1.5 1.5 0 0 1 8.5 6V3.5A1.5 1.5 0 0 1 10 2Z" />
    <path d="M9.5 10h5v3.2l-1.6 2.6h-1.8l-1.6-2.6V10Z" />
    <path d="M12 17.2c.9 1.3 1.9 2.4 1.9 3.3a1.9 1.9 0 0 1-3.8 0c0-.9 1-2 1.9-3.3Z" />
  </SvgIcon>
);

// A two-tone capsule, tilted.
const CapsuleIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <g transform="rotate(-45 12 12)">
      <path d="M7 7.8h5v8.4H7a4.2 4.2 0 0 1 0-8.4Z" />
      <path d="M12 7.8h5a4.2 4.2 0 0 1 0 8.4h-5V7.8Z" opacity=".45" />
    </g>
  </SvgIcon>
);

// A mound of powder with grains falling from a scoop.
const PowderIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M13 2.5h7.5a1 1 0 0 1 .8 1.6l-2.2 2.8a2 2 0 0 1-1.6.8H14a1 1 0 0 1-1-1V2.5Z" />
    <circle cx="14.6" cy="9.6" r="1" />
    <circle cx="12.4" cy="12" r=".9" />
    <circle cx="15.4" cy="12.6" r=".8" />
    <path d="M3 20.5c1.6-4.4 4.8-6.7 8.6-6.7 3.9 0 7.4 2.3 9.4 6.7a1 1 0 0 1-.9 1.5H3.9a1 1 0 0 1-.9-1.5Z" />
  </SvgIcon>
);

// A mouth-freshener spray bottle with a mist.
const MouthSprayIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path d="M9 2h4a1 1 0 0 1 1 1v1h2.2a.8.8 0 0 1 0 1.6H14V7h.5A1.5 1.5 0 0 1 16 8.5V9h-6v-.5A1.5 1.5 0 0 1 11.5 7H12V5.6H9.8a.8.8 0 0 1-.8-.8V2Z" />
    <path d="M9.2 10.5h7.6l.7 9.6a1.8 1.8 0 0 1-1.8 1.9H10.3a1.8 1.8 0 0 1-1.8-1.9l.7-9.6Z" />
    <circle cx="19.6" cy="3.6" r=".9" /><circle cx="21.4" cy="5.8" r=".8" /><circle cx="19.4" cy="7.4" r=".75" />
  </SvgIcon>
);

// A crimped cream tube with its cap at the bottom.
const CreamTubeIcon = (props) => (
  <SvgIcon {...props} viewBox="0 0 24 24">
    <path fillRule="evenodd" d="M6 2h12v2.2L15.6 17.5H8.4L6 4.2V2Zm3.3 5.2h5.4l-.7 5.6H10l-.7-5.6Z" />
    <path d="M9.3 18.5h5.4V21a1 1 0 0 1-1 1h-3.4a1 1 0 0 1-1-1v-2.5Z" />
  </SvgIcon>
);

const PRODUCT_FORMS = [
  { form: 'injection', label: 'Injection', pattern: /\bINJ(ECTION)?S?\b/i, Icon: VaccinesRounded },
  { form: 'drops', label: 'Drops', pattern: /\bDROPS?\b/i, Icon: DropsIcon },
  { form: 'syrup', label: 'Syrup', pattern: /\b(SYP|SYRUP)\b/i, Icon: MedicationLiquidRounded },
  { form: 'lotion', label: 'Lotion', pattern: /\b(LOTION|LOTN)\b/i, Icon: LotionIcon },
  { form: 'cream', label: 'Cream / ointment', pattern: /\b(CREAM|OINT|OINTMENT)\b/i, Icon: CreamTubeIcon },
  { form: 'mouth', label: 'Mouth paint / mouth care', pattern: /\b(MOUTH\s*PAINT|GUM\s*PAINT|MOUTH\s*WASH|MOUTHWASH|GARGLE)\b/i, Icon: MouthSprayIcon },
  { form: 'powder', label: 'Powder', pattern: /\b(POWDER|POW|PDR)\b/i, Icon: PowderIcon },
  { form: 'tablet', label: 'Tablet', pattern: /\b(TAB|TABS|TABLETS?)\b/i, Icon: TabletIcon },
  { form: 'capsule', label: 'Capsule', pattern: /\b(CAP|CAPS|CAPSULES?)\b/i, Icon: CapsuleIcon },
];
const DEFAULT_FORM = { form: 'medicine', label: 'Medicine', Icon: MedicationRounded };

// Dosage form read from the product name ("... TAB 10 TAB", "... SYP 60 ML", "... INJ 1 ML"); anything else is a general medicine.
// When a name has several form words ("ACAMPROL CAP 6 TAB"), the first one is the product's form; the last is usually the pack unit.
export const productForm = (name) => {
  let best = null;
  PRODUCT_FORMS.forEach((entry) => {
    const match = entry.pattern.exec(String(name || ''));
    if (match && (!best || match.index < best.index)) best = { index: match.index, entry };
  });
  return best ? best.entry : DEFAULT_FORM;
};

export const ProductFormIcon = ({ name }) => {
  const { form, label, Icon } = productForm(name);
  return (
    <Tooltip title={label}>
      <span className={`product-form-icon product-form-icon--${form}`} role="img" aria-label={label}>
        <Icon fontSize="small" />
      </span>
    </Tooltip>
  );
};
